import { z } from 'zod';
import { UuidString } from '../primitives.js';

/**
 * B-A.13 — структурные ErrorCode для нового error envelope.
 * Backend (после миграции error middleware) возвращает `{error: {code, trace_id, details}}`.
 * Frontend локализует через `m.error_by_code(code, details)`.
 *
 * Legacy `ApiError` (api-contracts.ts) с `message: string` — переходный период.
 * Новые endpoints используют `ApiErrorStrict` ниже.
 */
export const ErrorCode = z.enum([
  // ─── auth (6) ─────────────────────────────────────────────────────────
  'auth.missing_token',
  'auth.invalid_token',
  'auth.expired_token',
  // V-2 — неверные креды. Раньше login отдавал `auth.missing_token` (любой 401
  // сводился к нему через LEGACY_TO_DOT), и диагностика читала «токен не передан»
  // там, где на самом деле не совпал пароль.
  'auth.invalid_credentials',
  'auth.forbidden',
  'auth.password_change_required',
  // С-I6 Р.1 (В-54): под токеном подмены администрирование недоступно.
  'auth.impersonation_admin_forbidden',
  // ─── validation (5) ───────────────────────────────────────────────────
  'validation.invalid_body',
  'validation.invalid_query',
  'validation.invalid_param',
  'validation.zod_shape_mismatch',
  'validation.precondition_failed',
  // ─── resource (4) ─────────────────────────────────────────────────────
  'resource.not_found',
  'resource.conflict',
  'resource.locked',
  'resource.gone',
  // ─── resource dependency reasons (4) ─────────────────────────────────
  'resource.has_children',
  'resource.has_sle',
  'resource.has_stock_entries',
  'resource.has_work_orders',
  // ─── route (3) ────────────────────────────────────────────────────────
  'route.not_found',
  'route.method_not_allowed',
  'route.disabled',
  // ─── request (4) ──────────────────────────────────────────────────────
  'request.timeout',
  'request.body_too_large',
  'request.content_type_unsupported',
  'request.aborted',
  // ─── server (3) ───────────────────────────────────────────────────────
  'server.internal',
  // B77 (TCA-B4) — транзакция запроса прервана без ошибки ручки: записи не сохранены.
  'internal.transaction_aborted',
  'server.timeout',
  'server.degraded',
  // ─── integration (4) ──────────────────────────────────────────────────
  'integration.1c_unreachable',
  'integration.bitrix_unreachable',
  'integration.adesk_unreachable',
  'integration.temporal_unreachable',
  // ─── db (2) ───────────────────────────────────────────────────────────
  'db.unavailable',
  'db.constraint_violation',
  // ─── rate_limit (2) ───────────────────────────────────────────────────
  'rate_limit.exceeded',
  'rate_limit.tenant_quota',
  // ─── feature + maintenance (2) ───────────────────────────────────────
  'feature.disabled',
  'maintenance.in_progress',
  // ─── network (1) ─────────────────────────────────────────────────────
  'network.unavailable',
  // ─── domain: wo/stock/approval/valuation (8) ─────────────────────────
  'wo.invalid_state_transition',
  'wo.material_shortage',
  'wo.bom_not_found',
  'stock.insufficient_balance',
  'stock.negative_balance_forbidden',
  'approval.tier_mismatch',
  'approval.approver_insufficient',
  // С-I7 Р.2 (ACC-B36): под токеном подмены не решают заявки и не подписывают уровень 2.
  'approval.impersonation_forbidden',
  'valuation.unknown_method',
  // ─── client-side / unknown fallback (1) ──────────────────────────────
  'client.exception',
  // ─── task-module (BE-S12.2-13) ────────────────────────────────────────
  'task_module.no_default_state',
  'task_module.no_completed_state',
  'task_module.state_not_in_project',
  'task_module.field_key_duplicate',
  'task_module.cannot_claim_user_assignee',
  'task_module.already_claimed',
  'task_module.not_claim_owner',
  'task_module.circular_dependency',
  'task_module.dependency_duplicate',
  'task_module.self_reference',
  'task_module.cross_project_dependency',
  'task_module.cannot_demote_last_lead',
  'task_module.cannot_remove_last_lead',
  'task_module.not_comment_author',
  'task_module.comment_deleted',
  'task_module.task_archived',
  // I1.3 — коды, которые backend бросал, но enum их не содержал (error-path drift).
  'task_module.validation',
  // DR-F11 — запись baseline с неработающим в текущем составе задач методом расчёта.
  'task_module.method_unavailable',

  /* ── D1.5a — коды, разобранные из снимка error-code-coverage ──────────
   * Снимок держал 264 dotted-строки, и по нему нельзя было понять, что там долг.
   * Разбор по КОНТЕКСТУ употребления показал три разных класса: 151 имя
   * audit-инструмента (значения поля `tool` у emitAudit), 44 имени SSE-события и
   * контекст-ключа, и только 69 настоящих кодов ошибок. Тридцать два из них уже
   * имели ru+en тексты — то есть рендерились правильно, а в каноне не значились.
   * Они внесены сюда: enum перестаёт врать о полноте на треть настоящего долга. */
  'admin_jobs.scheduler_unavailable',
  'admin_jobs.unknown_job_name',
  'admin_user.account_status_platform_only', // С-I2 Р.6 (В-48): весь аккаунт — только платформа
  'admin_user.role_platform_only', // С-I6 Р.3 (В-54): глобальную роль меняет только платформа
  'admin_user.already_active',
  'admin_user.cannot_deactivate_last_admin',
  'admin_user.cannot_impersonate_admin',
  'admin_user.cannot_impersonate_self',
  // С-I5 П.2 (ACC-B14): цель не активна в компании, в которой начинают подмену.
  'admin_user.impersonate_target_not_in_entity',
  /* ── chat-interactive-actions B16/B17 — контур черновика действий чата ──
   * Карточка подтверждения живёт в переписке: её видно и через час, и в другой
   * вкладке. Коды отвечают на три разных вопроса человека, и сводить их в один
   * нельзя: «уже применено» (и вот результат), «предложение устарело», «отменено». */
  'chat_action.draft_already_consumed',
  'chat_action.draft_expired',
  'chat_action.draft_cancelled',
  /**
   * B26 (заход 3) — состав не совпал: тело запроса называет задачи, которых в
   * предложении не было. Провенанс (I7) держится на том, что `draft_id` в аудите
   * относится ровно к тому набору, который человек видел на карточке.
   */
  'chat_action.draft_composition_mismatch',
  /** B17 — слишком часто: массовые операции ограничены 20 в минуту на человека. */
  'task_module.bulk_rate_limited',
  'bank_import.draft_expired',
  'bank_import.draft_not_found',
  'bank_import.row_set_mismatch',
  'bank_import.token_mismatch',
  'fx_rate.delete_in_use',
  'fx_rate.delete_nbkz_forbidden',
  'integration.no_credentials',
  'project_move.has_invoices',
  'project_move.has_wo_worklogs',
  'project_move.needs_detach',
  'project_move.same_entity',
  'task_module.anchor_date_required',
  'task_module.assignee_not_project_member',
  'task_module.blueprint_kind_not_scratchable',
  'task_module.blueprint_lineage_mismatch',
  'task_module.blueprint_name_taken',
  'task_module.blueprint_not_active',
  'task_module.blueprint_not_found',
  'task_module.blueprint_version_conflict',
  'task_module.blueprint_version_stale',
  'task_module.inbox_cannot_be_private',
  'task_module.mixed_visibility_membership',
  'task_module.template_empty',
  'task_module.template_source_forbidden',
  'task_module.template_too_large',
  'task_move.has_wo_worklogs',
  'task_module.last_default_state',
  'task_module.email_is_existing_user',
  'task_module.project_lead_required',
  'task_module.author_not_in_scope',
  // BB3/BC2 — M2M membership пак task-multi-project-and-roles.
  'task_module.already_member',
  'task_module.cannot_remove_primary',
  'task_module.membership_cross_entity',
  'task_module.not_member',
  'task_module.endpoints_not_members',
  // B1 (gantt-table-config) — нет доступа к проекту удаляемой связи.
  'task_module.dependency_project_forbidden',
  // B1 (project-plan-change §3.1) — запертые поля запущенного проекта. План
  // переносится ручкой `plan-baseline` с обоснованием; контракт — только через заказ.
  'task_module.project_plan_locked',
  'task_module.project_contract_locked',
  // B6 — перенос срока применим только к запущенному проекту.
  'task_module.plan_shift_not_applicable',
  /**
   * Гейт фазы B (BLOCKER ревьюера) — перевёрнутый диапазон: конец плана раньше
   * начала. Ключ `error_task_module_due_before_start` уже жил в обеих локалях, но
   * в каноне кодов не значился и потому не локализовался нигде.
   */
  'task_module.due_before_start',
  /**
   * B20 (work-tasks-user-feedback §3.8) — таймер.
   *
   * `timer_already_running` — второй таймер на ТОЙ ЖЕ задаче; таймеры на разных
   * задачах параллельны (В-4), поэтому этот код означает гонку вкладок, а не
   * намерение, и FE лечит его рефетчем состояния.
   * `timer_not_found` — действие над таймером, которого уже нет (стоп после стопа).
   * `timer_wrong_state` — пауза приостановленного / возобновление бегущего.
   */
  'task_module.timer_already_running',
  'task_module.timer_not_found',
  'task_module.timer_wrong_state',
  /**
   * NC-B (свип tails-sweep-2, 2026-08-29) — 31 код, который обработчики бросали
   * годами, а канон не знал.
   *
   * Следствие было не «нет перевода», а хуже: FE локализует по коду из этого
   * enum, поэтому всё перечисленное ниже показывалось человеку как обезличенное
   * «Неизвестная ошибка. Trace: …» — при том что сервер знал точную причину и
   * присылал её кодом. Особенно обидно на финансовом контуре: «оплата больше
   * суммы документа» и «валюты не совпадают» — это подсказки к действию, а не
   * системный сбой.
   *
   * Снимок вёл храповик `tests/contracts/error-code-coverage-baseline.json`;
   * этой правкой он опустошён.
   */
  // ─── импорт банковской выписки (1) ────────────────────────────────────
  'bank_import.counterparty_invalid',
  // ─── бюджеты (5) ──────────────────────────────────────────────────────
  'budget.cannot_modify_active',
  // PLM-4 (пак `tails-closeout`, B17): недопустимый переход состояния бюджета.
  'budget.invalid_transition',
  'budget.not_editable',
  'budget.overlap_active',
  // PLM-4 (B17 (а)): правка поверх чужой — optimistic-check.
  'budget.stale',
  // ─── контрагенты (5) ──────────────────────────────────────────────────
  'counterparty.already_in_target',
  'counterparty.bin_iin_invalid',
  'counterparty.duplicate_bin_iin',
  'counterparty.has_open_documents',
  'counterparty.move_blocked_dependencies',
  // ─── курсы валют (1) ──────────────────────────────────────────────────
  'fx.nbkz_fetch_failed',
  // ─── счета (3) ────────────────────────────────────────────────────────
  'invoice.cannot_cancel_with_payments',
  'invoice.cannot_modify_posted',
  'invoice.cannot_push_before_post',
  // ─── фоновые задания (1) ──────────────────────────────────────────────
  'job.failed',
  // ─── платежи (4) ──────────────────────────────────────────────────────
  'payment.cannot_modify_posted',
  'payment.cannot_push_before_post',
  'payment.currency_mismatch',
  'payment.over_allocation',
  // ─── создание проекта (1) ─────────────────────────────────────────────
  'project_create.validation_failed',
  // ─── взаиморасчёты (4) ────────────────────────────────────────────────
  'settlement.cannot_push_before_post',
  'settlement.lines_currency_mismatch',
  'settlement.not_editable',
  'settlement.not_found',
  // ─── складские документы (2) ──────────────────────────────────────────
  'stock_entry.not_editable',
  'stock_reconciliation.not_editable',
  'stock_reconciliation.voucher_duplicate',
  // F53 (WHS-F21): строка причины не из этого черновика.
  'stock_reconciliation.line_not_found',
  // ─── task-модуль (6) ──────────────────────────────────────────────────
  'task_module.attachment_not_in_task',
  'task_module.folder_dates_forbidden',
  'task_module.folder_progress_forbidden',
  'task_module.membership_has_dependencies',
  'task_module.move_declassify_lead_required',
  'task_module.subject_orphan',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

/** Structural error — без freeform `message`. Используется новыми endpoints. */
export const ApiErrorStrict = z
  .object({
    code: ErrorCode,
    trace_id: UuidString,
    details: z.record(z.unknown()).optional(),
  })
  .strict();
export type ApiErrorStrict = z.infer<typeof ApiErrorStrict>;

export const ApiErrorStrictEnvelope = z
  .object({
    error: ApiErrorStrict,
  })
  .strict();
export type ApiErrorStrictEnvelope = z.infer<typeof ApiErrorStrictEnvelope>;
